import { sum } from 'lodash';

import {
  type UnitRelationLookupOptions,
  type UnitRelationOptionInput,
} from './RelationDivisionLine.types';
import { type ShareValues } from '@/containers/Finance/Invoices/hoa/shared/types/formTypes';

import CONSTANTS from './RelationDivisionLine.constants';

import { createUnitRelationKey } from '~/common/utils/unitRelationUtils';
import { roundTo } from '@/shared/utils/helpers';

type RelationRecord<T> = {
  [key: string]: T;
};

type ShareRelationInput = Pick<ShareValues, 'buildingRelationId' | 'unitId'>;

type DistributeByFractionsInput = {
  fractions: number[];
  precision: number;
  total: number;
};

// spread the rounding difference vs putting everything on the last share
// => smaller decimal differences
const distributeTotalByFractions = (input: DistributeByFractionsInput) => {
  const { fractions, precision, total } = input;

  let cumFraction = 0;
  let prevCumValue = 0;

  return fractions.map((fraction) => {
    cumFraction += fraction;

    const cumValue = roundTo(total * cumFraction, precision);
    const value = roundTo(cumValue - prevCumValue, precision);

    prevCumValue = cumValue;

    return value;
  });
};

const getShareRelationKey = ({ buildingRelationId, unitId }: ShareRelationInput) => {
  if (!buildingRelationId || !unitId) return null;

  return createUnitRelationKey({ buildingRelationId, unitId });
};

const relationLookupOptionsToKey = (lookupOptions: UnitRelationLookupOptions) => {
  if ('key' in lookupOptions) return lookupOptions.key;

  return createUnitRelationKey({
    buildingRelationId: lookupOptions.buildingRelationId,
    unitId: lookupOptions.unitId,
  });
};

const roundDivisionAmount = (amount: number) => roundTo(amount, CONSTANTS.DECIMAL_PRECISION.AMOUNT);

const sharesToFractions = (shares: number[]) => {
  const totalShares = sum(shares);

  const calcFraction = (share: number) => (totalShares ? share / totalShares : 0);

  return shares.map(calcFraction);
};

const getDivisionRelationKeys = (divisions: ShareRelationInput[]) => {
  const keys: string[] = [];

  divisions.forEach((division) => {
    const key = getShareRelationKey(division);

    if (key) keys.push(key);
  });

  return keys;
};

const toDivisionAmount = (totalAmount: number, fraction: number) =>
  roundDivisionAmount(totalAmount * fraction);

const toDivisionPercentage = (fraction: number) =>
  roundTo(fraction * 100, CONSTANTS.DECIMAL_PRECISION.PERCENTAGE);

type ExcludedRelationsInput = { divisions: ShareRelationInput[] } | { keys: string[] };

type ExcludedRelationsHasOptions = {
  except?: UnitRelationLookupOptions;
};

const createExcludedRelationKeysLookup = (input: ExcludedRelationsInput) => {
  const parsedKeys = 'keys' in input ? input.keys : getDivisionRelationKeys(input.divisions);

  const set = new Set(parsedKeys);

  const has = (key: string, options?: ExcludedRelationsHasOptions) => {
    if (!set.has(key)) return false;

    return options?.except === undefined || key !== relationLookupOptionsToKey(options.except);
  };

  return { has, set };
};

const createUnitOptionLookup = <T extends UnitRelationOptionInput>(options: readonly T[]) => {
  const lookup: RelationRecord<T> = {};

  options.forEach((option) => {
    lookup[option.value.key] = option;
  });

  const get = (currentDivision: UnitRelationLookupOptions) => {
    const lookupKey = relationLookupOptionsToKey(currentDivision);

    return lookup[lookupKey];
  };

  return {
    get,
    lookup,
  };
};

const splitAmountByFractions = (totalAmount: number, fractions: number[]) =>
  distributeTotalByFractions({
    fractions,
    precision: CONSTANTS.DECIMAL_PRECISION.AMOUNT,
    total: totalAmount,
  });

const splitPercentageByFractions = (fractions: number[]) =>
  distributeTotalByFractions({
    fractions,
    precision: CONSTANTS.DECIMAL_PRECISION.PERCENTAGE,
    total: 100,
  });

const internal = {
  distributeTotalByFractions,
  getShareRelationKey,
  relationLookupOptionsToKey,
  roundDivisionAmount,
};

export {
  createExcludedRelationKeysLookup,
  createUnitOptionLookup,
  internal,
  getDivisionRelationKeys,
  getShareRelationKey,
  sharesToFractions,
  splitAmountByFractions,
  splitPercentageByFractions,
  toDivisionAmount,
  toDivisionPercentage,
};
